import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Skeleton from '@mui/material/Skeleton'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import ManageHeadOfOrganizationModal from './ManageHeadOfOrganizationModal'
import AddDepartmentModal from './AddDepartmentModal'
import defaultAvatar from 'assets/images/default_avatar.png'
import ErrorAddLayerModal from 'module/department-configuration/ErrorAddLayerModal'

import {
  deleteDepartmentLayer,
  getDepartmentList,
  postDepartmentLayer,
} from 'services/api/department-configuration'
import { useQuery, useMutation, useQueryClient } from 'react-query'

import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import AdxTableComponent from 'components/reusable/table/AdxTableComponent'
import DeleteDepartmentModal from 'components/reusable/modal/DeleteDepartmentModal'
import AddIcon from '@mui/icons-material/Add'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import AdxButton from 'components/custom/AdxButton'

const DepartmentConfiguration = () => {
  const queryClient = useQueryClient()
  const [showManageHeadOfOrganizationModal, setShowManageHeadOfOrganizationModal] = useState(false)
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false)
  const [structureId, setStructureId] = useState(null)
  const [selectedCardId, setSelectedCardId] = useState(null)
  const [activeLayerIndex, setActiveLayerIndex] = useState(0)
  const [showDeleteLayerModal, setShowDeleteLayerModal] = useState(false)
  const [deleteLayerError, setDeleteLayerError] = useState('')

  const handleClickManageHeadOfOrganizationModal = () => {
    setShowManageHeadOfOrganizationModal(true)
  }

  const handleCloseManageHeadOfOrganizationModal = () => {
    setShowManageHeadOfOrganizationModal(false)
  }

  const handleCloseAddDepartmentModal = () => {
    setShowAddDepartmentModal(false)
  }

  const handleClickAddDepartment = (item, index) => {
    setShowAddDepartmentModal(true)
    setStructureId(item?.structure_id)
  }

  const handleClickManageDepartment = (item) => {
    setShowAddDepartmentModal(true)
    setSelectedCardId(item?.id)
  }

  const [isModalOpen, setModalOpen] = useState(false)
  const handleOpen = () => {
    setModalOpen(true)
  }
  const handleClose = () => {
    setModalOpen(false)
  }

  const departmentList = useQuery('departmentList', async () => {
    const { data } = await getDepartmentList()
    const newData = {
      head_of_organisation: data?.[0].departments[0],
      layers: data?.slice(1),
    }
    return newData
  })

  const handleAddTab = async (departmentList) => {
    const values = {
      name: `Layer ${departmentList?.data?.layers?.length + 1}`,
      hierarchy: departmentList?.data?.layers?.length + 1,
    }
    await mutation.mutate(values)
  }

  const mutation = useMutation(postDepartmentLayer, {
    onSuccess: async () => {
      await queryClient.invalidateQueries('departmentList')
    },
    onError: (e) => {
      if (e.status === 422) {
        handleOpen()
      } else {
        console.log(e.data.message)
      }
    },
  })

  const deleteLayerMutation = useMutation((id) => deleteDepartmentLayer(id), {
    onSuccess: async () => {
      setShowDeleteLayerModal(false)
      setDeleteLayerError('')
      setActiveLayerIndex((current) => Math.max(current - 1, 0))
      await queryClient.invalidateQueries('departmentList')
    },
    onError: (e) => {
      setDeleteLayerError(e?.data?.message || 'Unable to delete layer')
      setShowDeleteLayerModal(true)
    },
  })

  const layers = departmentList?.data?.layers || []
  const activeLayer = layers[activeLayerIndex]
  const isLastLayer = activeLayerIndex === layers.length - 1
  const activeLayerHasDepartments = Boolean(activeLayer?.departments?.length)
  const canDeleteActiveLayer = Boolean(activeLayer) && activeLayerIndex > 0 && isLastLayer && !activeLayerHasDepartments

  const getDeleteLayerHelp = () => {
    if (!activeLayer) return 'Select a layer to delete'
    if (activeLayerIndex === 0) return 'Layer 1 cannot be deleted'
    if (!isLastLayer) return 'Only the last layer can be deleted'
    if (activeLayerHasDepartments) {
      return `${activeLayer.layer_name} has departments. Move or delete those departments first.`
    }
    return `Delete ${activeLayer.layer_name}`
  }

  const handleDeleteActiveLayer = () => {
    if (!canDeleteActiveLayer) return

    setDeleteLayerError('')
    setShowDeleteLayerModal(true)
  }

  const handleConfirmDeleteLayer = () => {
    if (!canDeleteActiveLayer) return
    deleteLayerMutation.mutate(activeLayer.structure_id)
  }

  const handleCloseDeleteLayerModal = () => {
    if (deleteLayerMutation.isLoading) return
    setShowDeleteLayerModal(false)
    setDeleteLayerError('')
  }

  const tableColumn = [
    {
      id: 1,
      Header: 'Department',
      accessor: 'name',
    },
    {
      id: 2,
      Header: 'Sub department of',
      accessor: 'parent',
    },
    {
      id: 3,
      Header: 'Head of department',
      accessor: 'head',
      Cell: (props) => {
        const { value } = props
        return (
          <Box display="flex" gap={2}>
            <Avatar src={value?.profile_image_url || defaultAvatar} />
            <Box width={200}>
              <Typography variant="body1" textAlign="left" noWrap>
                {value?.name}
              </Typography>
              <Typography variant="body2" textAlign="left" noWrap>
                {value?.designation}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
  ]

  const tableFunction = (item) => {
    handleClickManageDepartment(item)
  }

  return (
    <>
      <Box display="flex" width="100%" flexDirection="column" gap={3}>
        <Box px={{ xs: 2, lg: 0 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography variant="h4" mb={0} fontWeight="bold">
              Head of Organization
            </Typography>
            <Typography variant="body1" color="light-gray-text.main">
              Head of Organization is the top level. Add layers below it to build your reporting
              structure.
            </Typography>
          </Box>
          <Box
            px={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            py={2}
            bgcolor="white.main"
            sx={{ border: 1, borderRadius: '4.5px', borderColor: 'gray-line.main' }}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body1" color="light-gray-text.main" mr={12}>
                Head of Organization
              </Typography>
              <Box display="flex" gap={2}>
                {!departmentList?.isLoading ? (
                  <Avatar
                    src={
                      departmentList?.data?.head_of_organisation?.head?.profile_image_url ||
                      defaultAvatar
                    }
                  />
                ) : (
                  <Skeleton width={40} height={40} variant="circular" />
                )}
                <Box>
                  {!departmentList?.isLoading ? (
                    <Typography variant="body1" color="dark-text.main">
                      {departmentList?.data?.head_of_organisation?.head?.name}
                    </Typography>
                  ) : (
                    <Skeleton width={200} />
                  )}
                  {!departmentList?.isLoading ? (
                    <Typography variant="body2" color="light-gray-text.main">
                      {departmentList?.data?.head_of_organisation?.name}
                    </Typography>
                  ) : (
                    <Skeleton width={150} />
                  )}
                </Box>
              </Box>
            </Box>
            <Typography
              onClick={() => handleClickManageHeadOfOrganizationModal()}
              mx={2}
              noWrap
              variant="subtitle1"
              color={'alternate.main'}
              sx={{
                textDecoration: 'underline',
                fontWeight: 'medium',
                cursor: 'pointer',
              }}
            >
              Manage
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'white.main',
              border: 1,
              borderColor: 'gray-line.main',
              borderRadius: '4.5px',
            }}
          >
            <AdxTabsBadge
              initialValue={0}
              queryParams={activeLayerIndex}
              setTabIndex={setActiveLayerIndex}
              scrollButtons={false}
              addButton={
                <Box display="flex" alignItems="center">
                  <IconButton
                    onClick={() => handleAddTab(departmentList)}
                    sx={{
                      borderRadius: '4.5px',
                      border: 1,
                      p: 0.5,
                      borderColor: 'light-gray-text.main',
                    }}
                  >
                    <AddIcon color="light-gray-text" sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              }
              headerAction={
                <Tooltip title={getDeleteLayerHelp()}>
                  <span>
                    <IconButton
                      disabled={!canDeleteActiveLayer || deleteLayerMutation.isLoading}
                      onClick={handleDeleteActiveLayer}
                      size="small"
                      aria-label="Delete selected layer"
                    >
                      <RemoveCircleIcon
                        color={canDeleteActiveLayer ? 'error' : 'disabled'}
                        sx={{ fontSize: 20 }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              {layers.map((item, index) => (
                <AdxTabsPanel label={item?.layer_name} key={index}>
                  <AdxTableComponent
                    column={tableColumn}
                    data={item.departments || []}
                    nav="Manage"
                    onClick={tableFunction}
                    sxNav={{
                      textDecoration: 'underline',
                      fontWeight: 'medium',
                      cursor: 'pointer',
                    }}
                  />
                  {!item?.departments?.length ? (
                    <Box
                      minHeight={75}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      borderTop={1}
                      borderColor="gray-line.main"
                      px={2}
                    >
                      <Typography variant="body1" color="light-gray-text-2.main" textAlign="center">
                        No departments in this layer yet. Add a department here, or delete this
                        layer if it was created by mistake.
                      </Typography>
                    </Box>
                  ) : null}
                  <Box p={0.5}>
                    <AdxButton
                      variant={'ghost'}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                      }}
                      onClick={() => handleClickAddDepartment(item, index)}
                    >
                      <AddIcon color="alternate" />
                      <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                        Add department
                      </Typography>
                    </AdxButton>
                  </Box>
                </AdxTabsPanel>
              ))}
            </AdxTabsBadge>
          </Box>
        </Box>
      </Box>
      <ManageHeadOfOrganizationModal
        showManageHeadOfOrganizationModal={showManageHeadOfOrganizationModal}
        handleCloseManageHeadOfOrganizationModal={handleCloseManageHeadOfOrganizationModal}
        headData={departmentList?.data?.head_of_organisation}
      />

      <AddDepartmentModal
        showAddDepartmentModal={showAddDepartmentModal}
        handleCloseAddDepartmentModal={handleCloseAddDepartmentModal}
        setStructureId={setStructureId}
        structureId={structureId}
        setSelectedCardId={setSelectedCardId}
        selectedCardId={selectedCardId}
      />

      <ErrorAddLayerModal isModalOpen={isModalOpen} handleClose={handleClose} />
      <DeleteDepartmentModal
        open={showDeleteLayerModal}
        onClose={handleCloseDeleteLayerModal}
        onConfirm={handleConfirmDeleteLayer}
        title={`Delete ${activeLayer?.layer_name || 'layer'}?`}
        subtitle="This will remove the empty layer from the department structure."
        errorMessage={deleteLayerError}
        isLoading={deleteLayerMutation.isLoading}
      />
    </>
  )
}

export default DepartmentConfiguration
